import { ApolloError } from "@apollo/client";
import { Alert, Box, Stack } from "@mui/material";
import { To } from "react-router";

import { GqlPlannerV2VisitPlannedOnDate, GqlPlannerVisitStatus } from "~/__generated__";
import { MapPanel, MapPanelHeader, MapPanelScrollableArea } from "~/modules/map/components/MapPanel";

import { FieldworkerAssignments } from "../../../../../components/FieldworkerAssignments";
import { FieldworkerAssignmentsHorizontalSkeleton } from "../../../../../components/FieldworkerAssignments/FieldworkerAssignmentsHorizontal";
import { FieldworkerAssignmentsFragment } from "../../../../../components/FieldworkerAssignments/fragments.generated";
import { VisitTravelGroupsOverview } from "../../../../../components/VisitTravelGroupsOverview";
import { VisitTravelGroupsOverviewTravelGroupFragment } from "../../../../../components/VisitTravelGroupsOverview/fragments.generated";
import {
    TravelGroupCardProps,
    TravelGroupCardSkeleton,
} from "../../../../../components/VisitTravelGroupsOverview/TravelGroupCard";
import { PlannerV2VisitGetByIdQuery } from "../../queries.generated";
import { VisitDetailPanelHeader } from "./VisitDetailPanelHeader";
import { VisitInfoSection, VisitInfoSectionSkeleton } from "./VisitInfoSection";
import { VisitPlannedOnDatesPanel } from "./VisitPlannedOnDates";
import { VisitStatusSection } from "./VisitStatusSection";
import { VisitTimeslot, VisitTimeslotSkeleton } from "./VisitTimeslot";
import { VisitTimeslotSelector } from "./VisitTimeslotSelector";

type VisitDetailPanelProps = {
    visit?: PlannerV2VisitGetByIdQuery["plannerV2VisitGetById"];
    possibleTimeslots?: PlannerV2VisitGetByIdQuery["plannerV2VisitGetPossibleTimes"];
    selectedTimeslotId: string | undefined;
    onTimeslotChange: (timeslotId: string) => void;
    travelGroups: VisitTravelGroupsOverviewTravelGroupFragment[];
    date: string;
    assignedFieldworkers: FieldworkerAssignmentsFragment[];
    onTravelGroupCancelClick: TravelGroupCardProps["onTravelGroupCancelClick"];
    onShiftInTravelGroupCancelClick: TravelGroupCardProps["onShiftInTravelGroupCancelClick"];
    travelGroupOpenHref: (travelGroupId: string) => To;
    loadingError?: ApolloError;
    loading: boolean;
    isCancelingShiftId: string | undefined;
    isCancelingGroupId: string | undefined;
    isFocusedOnSpecificDate: boolean;
    isOnPriorDate: boolean;
    plannedOnDates: GqlPlannerV2VisitPlannedOnDate[];
    onSelectPlannedOnDate: (value: Date) => void;
    backHref: To;
    selectedDate: Date;
};

export function VisitDetailPanel({
    visit,
    possibleTimeslots,
    selectedTimeslotId,
    onTimeslotChange,
    travelGroups,
    assignedFieldworkers,
    travelGroupOpenHref,
    onTravelGroupCancelClick,
    onShiftInTravelGroupCancelClick,
    loadingError,
    loading,
    isCancelingShiftId,
    isCancelingGroupId,
    isFocusedOnSpecificDate,
    isOnPriorDate,
    plannedOnDates,
    onSelectPlannedOnDate,
    backHref,
    selectedDate,
}: VisitDetailPanelProps) {
    const fixedTimeslot = possibleTimeslots?.find(timeslot => timeslot._id === visit?.timeslotId);

    return (
        <MapPanel>
            <MapPanelHeader>
                <VisitDetailPanelHeader area={visit?.plannerArea} backHref={backHref} loading={loading} />
            </MapPanelHeader>

            {loading ? (
                <VisitDetailPanelSkeleton />
            ) : (
                <>
                    {loadingError && <Alert severity="error">{loadingError.message}</Alert>}
                    {loadingError === undefined && visit && (
                        <>
                            <VisitInfoSection
                                plannerStatus={visit.plannerStatus}
                                protocolName={visit.protocol.name}
                                protocolVisit={visit.protocolVisit}
                                hasStagedChanges={visit.hasStagedChanges}
                                showReviewToolButton={
                                    travelGroups && travelGroups.length > 0 && visit.status !== GqlPlannerVisitStatus.Unplanned
                                }
                                reviewToolButtonHref={`/c/${visit?.plannerArea.project.client.slug}/p/${visit?.plannerArea.project.slug}/review-visit/${visit?.id}`}
                            />

                            {!isFocusedOnSpecificDate ? (
                                <VisitPlannedOnDatesPanel
                                    currentDate={selectedDate}
                                    elements={plannedOnDates}
                                    isOnPriorDate={isOnPriorDate}
                                    isReplannable={visit.status === GqlPlannerVisitStatus.Unplanned && visit.startTime === null}
                                    onClick={onSelectPlannedOnDate}
                                    onReplanClick={() => onSelectPlannedOnDate(selectedDate)}
                                />
                            ) : (
                                <>
                                    <VisitStatusSection
                                        onClick={onSelectPlannedOnDate}
                                        possibleDays={visit.possibleDays}
                                        selectedDate={selectedDate}
                                        plannerStatus={visit.plannerStatus}
                                    />

                                    {possibleTimeslots &&
                                        (fixedTimeslot ? (
                                            <VisitTimeslot startTime={fixedTimeslot.startTime} endTime={fixedTimeslot.endTime} />
                                        ) : (
                                            <VisitTimeslotSelector
                                                possibleTimeslots={possibleTimeslots}
                                                selectedTimeslotId={selectedTimeslotId}
                                                onChange={onTimeslotChange}
                                            />
                                        ))}

                                    <MapPanelScrollableArea
                                        sx={theme => ({
                                            borderTop: `1px solid ${theme.palette.divider}`,
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                        })}
                                    >
                                        <VisitTravelGroupsOverview
                                            travelGroups={travelGroups}
                                            onTravelGroupCancelClick={onTravelGroupCancelClick}
                                            onShiftInTravelGroupCancelClick={onShiftInTravelGroupCancelClick}
                                            travelGroupOpenHref={travelGroupOpenHref}
                                            isCancelingShiftId={isCancelingShiftId}
                                            isCancelingGroupId={isCancelingGroupId}
                                        />
                                    </MapPanelScrollableArea>

                                    <Box padding={2}>
                                        <FieldworkerAssignments
                                            fieldworkers={assignedFieldworkers}
                                            fieldworkersRequired={visit.requiredFieldworkers}
                                        />
                                    </Box>
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </MapPanel>
    );
}

function VisitDetailPanelSkeleton() {
    return (
        <>
            <VisitInfoSectionSkeleton />

            <VisitTimeslotSkeleton />

            <MapPanelScrollableArea
                sx={theme => ({
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                })}
            >
                <Stack
                    component="ul"
                    margin={0}
                    padding={2}
                    sx={theme => ({
                        backgroundColor: theme.palette.background.default,
                    })}
                    rowGap={2}
                >
                    <TravelGroupCardSkeleton />
                </Stack>
            </MapPanelScrollableArea>

            <Box padding={2}>
                <FieldworkerAssignmentsHorizontalSkeleton />
            </Box>
        </>
    );
}
